import React, { FC } from 'react';
import { useMarketMakerPools } from '@app/hooks/merkl/useMarketMakerPools';
import { useAlgebraPoolNitroDetails } from '@app/hooks/nitro/useAlgebraPoolNitroDetails';
import { getMarketMakerParams } from '@app/hooks/pools/usePreparedPoolsRows';
import { useMarketMakerRewards } from '@app/hooks/merkl/useMarketMakerRewards';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { Icon } from '@app/components/Icon';
import { Badge } from '@app/components/Badge';
import { SiMediafire } from 'react-icons/si';
import { formatUSD } from '@app/helpers/formatUSD';
import { formatNumber } from '@app/helpers/format';
import { Address } from '@thirdweb-dev/sdk';
import { Currency } from '@cryptoalgebra/kim-sdk';

interface Props {
  poolAddress: Address;
  currencyA: Currency;
  currencyB: Currency;
  nftPoolAddress: string;
}

export const NitroDetails: FC<Props> = ({
  currencyA,
  currencyB,
  nftPoolAddress
}) => {
  const marketMakerPools = useMarketMakerPools();

  const data = useAlgebraPoolNitroDetails(nftPoolAddress);

  const { marketMaker } = getMarketMakerParams(
    marketMakerPools,
    currencyA.wrapped.address,
    currencyB.wrapped.address
  );

  const { totalNitroRewardsUSD, nitroRewards } = useMarketMakerRewards(
    marketMaker?.merkl,
    data?.id
  );

  const grouped = nitroRewards.reduce<Record<string, number>>((r, item) => {
    if (r[item.rewardsToken.symbol]) {
      r[item.rewardsToken.symbol] += +item.dailyRewards;
    } else {
      r[item.rewardsToken.symbol] = +item.dailyRewards;
    }

    return r;
  }, {});

  return (
    <>
      {data && (
        <>
          <VStack
            sx={{
              mt: 3,
              padding: '24px',
              backgroundColor: 'gray.2',
              borderRadius: '10px',
              color: 'nitro',
              fontSize: 14,
              width: '100%',
              // maxWidth: ['100vw', '400px'],
              alignItems: 'flex-start'
            }}
          >
            <Badge
              variant="nitro_pool"
              tooltip={<span>Nitro Pool Requirements</span>}
              iconComponent={
                <SiMediafire size={20} color="var(--chakra-colors-nitro)" />
              }
              label={<span>Nitro details</span>}
            />
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="nitro">
                TVL:
              </Text>
              <Text color="neutral.100" fontSize="12px">
                {data.tvl}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="nitro">
                Duration:
              </Text>
              <Text color="neutral.100" fontSize="12px">
                {data.duration}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="nitro">
                APR:
              </Text>
              <Text color="neutral.100" fontSize="12px">
                {data.apr}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="nitro">
                Min lock:
              </Text>
              <Text color="neutral.100" fontSize="12px">
                {data.minLock} days
              </Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="nitro">
                Pending rewards:
              </Text>
              <Text color="neutral.100" fontSize="12px">
                {formatUSD.format(+data.pendingRewards)}
              </Text>
            </HStack>
            <HStack justifyContent="space-between" width="100%">
              <Text fontSize="12px" color="warning.100">
                Daily MM rewards:
              </Text>
              <HStack alignItems="center" gap={1}>
                <Text color="neutral.100" fontSize="12px">
                  {Object.keys(grouped).map(symbol => {
                    const value = grouped[symbol];

                    return (
                      <div key={symbol}>
                        {formatNumber(value)} {symbol}
                      </div>
                    );
                  })}
                </Text>
                <Text color="neutral.100" fontSize="12px">
                  ({formatUSD.format(totalNitroRewardsUSD)})
                </Text>
              </HStack>
            </HStack>
          </VStack>
          <Box
            sx={{
              background: 'neutral.800',
              width: '100%',
              p: '24px',
              borderRadius: '8px',
              mt: 4
            }}
          >
            <HStack
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Text color="nitro" fontSize={16}>
                Nitro pool
              </Text>
            </HStack>
            <Text fontSize={14} color="gray.15" mt={5}>
              Earn more rewards on your LP positions in Kim by leveraging your
              kpNFTs - These are NFTs you receive that act like a “receipt,” of
              sorts, for your liquidity positions on Kim. With Nitro Pools,
              you’ll be able to put them to work. You’ll be able to stake that
              kpNFT or LP in a Nitro Pool to earn additional rewards. By staking
              your kpNFTs, you can earn explosive rewards from a Nitro Pool. All
              entirely permissionless.
            </Text>
          </Box>
        </>
      )}
    </>
  );
};
